import React, { useEffect, useRef, useState } from 'react';
import { apiFetch, errorText } from '../shared/api';
import { formatPhoneUsDisplay } from '../add/formatPhoneUsDisplay';
import Spinner from '../shared/Spinner';
import LoadFailure from '../shared/LoadFailure';

// A compact, filterable dropdown replaces the native <select>: its list is capped
// to a scroll window (so a long distributor list is not full-page tall) and the
// input is narrow rather than as wide as the card.
export default function DistributorCard({ demo, endpoints, onDemoChange, onToast }) {
    const [distributors, setDistributors] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [loading, setLoading] = useState(true);
    const [changing, setChanging] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [confirmMessage, setConfirmMessage] = useState(null);
    const blurTimerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => () => clearTimeout(blurTimerRef.current), []);

    // Prefetch on mount so the dropdown is populated the moment it opens.
    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setLoadFailed(false);
        apiFetch(endpoints.distributors).then(({ status, json }) => {
            if (cancelled) {
                return;
            }
            if (status >= 200 && status < 300) {
                setDistributors(json?.data || []);
            } else {
                // A background prefetch failing shouldn't blast a toast on page load;
                // surface it to the console and let the dropdown show the retry row.
                setLoadFailed(true);
                console.error('DistributorCard: failed to prefetch distributors', errorText(json, ''));
            }
        }).catch((error) => {
            if (!cancelled) {
                setLoadFailed(true);
                console.error('DistributorCard: failed to prefetch distributors', error);
            }
        }).finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoints.distributors, retryCount]);

    const handleFocus = () => {
        clearTimeout(blurTimerRef.current);
        setOpen(true);
    };

    // Close on blur, but after a beat so a click on an option still registers.
    // Focus moving onto something inside the list (the Retry button) keeps it open.
    const handleBlur = (event) => {
        if (listRef.current?.contains(event.relatedTarget)) {
            return;
        }
        blurTimerRef.current = setTimeout(() => {
            setOpen(false);
            setActiveIndex(-1);
        }, 150);
    };

    const handleQueryChange = (event) => {
        setQuery(event.target.value);
        setSelectedId('');
        setConfirmMessage(null);
        setActiveIndex(-1);
        setOpen(true);
    };

    const selectDistributor = (distributor) => {
        setSelectedId(String(distributor.id));
        setQuery(distributor.name);
        setConfirmMessage(null);
        setActiveIndex(-1);
        setOpen(false);
    };

    const changeDistributor = (confirm = false) => {
        if (!selectedId) {
            return;
        }
        setConfirmMessage(null);
        setChanging(true);

        apiFetch(endpoints.changeDistributor, {
            method: 'POST',
            body: JSON.stringify(confirm ? { distributor_id: selectedId, confirm_duplicate: true } : { distributor_id: selectedId }),
            headers: { 'Content-Type': 'application/json' },
        }).then(({ status, json }) => {
            if (status >= 200 && status < 300) {
                onDemoChange(json.data);
                setSelectedId('');
                setQuery('');
                setConfirmMessage(null);

                if (Array.isArray(json.warnings) && json.warnings.length > 0) {
                    onToast({ type: 'warning', text: json.warnings.join(' ') });
                } else {
                    onToast({ type: 'success', text: __('Distributor changed') });
                }
            } else if (status === 422 && json?.errors?.confirm_duplicate) {
                setConfirmMessage(json.errors.confirm_duplicate[0]);
            } else if (status === 422 && json?.errors?.distributor_id?.[0]) {
                onToast({ type: 'error', text: json.errors.distributor_id[0] });
            } else {
                onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
            }
        }).catch((error) => {
            console.error(error);
            onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
        }).finally(() => {
            setChanging(false);
        });
    };

    const needle = query.trim().toLowerCase();
    // Once a distributor is picked the query equals its name; don't filter it down
    // to a single row - show the full list so the user can pick another.
    const filtered = (needle === '' || selectedId)
        ? distributors
        : distributors.filter((distributor) => distributor.name.toLowerCase().includes(needle));
    const options = loading ? [] : filtered;
    const activeOption = open ? options[activeIndex] : undefined;
    const optionId = (distributor) => `demo-edit-distributor-option-${distributor.id}`;
    const activeOptionId = activeOption ? optionId(activeOption) : undefined;

    useEffect(() => {
        if (activeOptionId) {
            document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
        }
    }, [activeOptionId]);

    const handleRetry = () => {
        setRetryCount((count) => count + 1);
        // The Retry button unmounts while the list reloads; hand focus back to the
        // input so a later blur still closes the list.
        inputRef.current?.focus();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            if (options.length === 0) {
                return;
            }
            const last = options.length - 1;
            if (event.key === 'ArrowDown') {
                setActiveIndex((index) => (index >= last ? 0 : index + 1));
            } else {
                setActiveIndex((index) => (index <= 0 ? last : index - 1));
            }
        } else if (event.key === 'Enter') {
            if (activeOption) {
                event.preventDefault();
                selectDistributor(activeOption);
            }
        } else if (event.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
        }
    };

    return (
        <div className="card demo-edit-distributor" data-testid="demo-edit-distributor">
            <div className="card-body">
                <h5 className="card-title">{__('Current Distributor')}</h5>
                <p>{demo.owner.name}</p>
                <p>{demo.owner.email}</p>
                <p>{formatPhoneUsDisplay(demo.owner.phone)}</p>

                <div className="form-group demo-edit-distributor-select" style={{ maxWidth: 360, position: 'relative' }}>
                    <input
                        ref={inputRef}
                        type="text"
                        role="combobox"
                        aria-label={__('Change Distributor')}
                        aria-expanded={open}
                        aria-controls="demo-edit-distributor-listbox"
                        aria-activedescendant={activeOptionId}
                        autoComplete="off"
                        className="form-control"
                        placeholder={__('Change Distributor')}
                        value={query}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleQueryChange}
                        onKeyDown={handleKeyDown}
                    />

                    {open &&
                        <ul
                            ref={listRef}
                            id="demo-edit-distributor-listbox"
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            className="list-group demo-edit-distributor-list"
                            role="listbox"
                            style={{ position: 'absolute', zIndex: 10, width: '100%' }}
                        >
                            {loading &&
                                <li className="list-group-item text-muted">
                                    <Spinner label={__('Loading the distributor list')} className="mr-2" />
                                    {__('Loading distributors...')}
                                </li>
                            }
                            {!loading && loadFailed &&
                                <li className="list-group-item" role="presentation" onMouseDown={(event) => event.preventDefault()}>
                                    <LoadFailure message={__('Could not load distributors')} onRetry={handleRetry} />
                                </li>
                            }
                            {!loading && !loadFailed && filtered.length === 0 &&
                                <li className="list-group-item text-muted">{__('No distributors found')}</li>
                            }
                            {options.map((distributor) => (
                                <li
                                    key={distributor.id}
                                    id={optionId(distributor)}
                                    role="option"
                                    aria-selected={String(distributor.id) === selectedId}
                                    className={`list-group-item list-group-item-action${String(distributor.id) === selectedId ? ' active' : ''}${distributor === activeOption ? ' list-group-item-primary' : ''}`}
                                    // mousedown fires before the input's blur, so the pick
                                    // registers before the list is scheduled to close.
                                    onMouseDown={(event) => { event.preventDefault(); selectDistributor(distributor); }}
                                >
                                    {distributor.name}
                                </li>
                            ))}
                        </ul>
                    }
                </div>

                <span className={`demo-island-btn-guard${!selectedId ? ' is-disabled' : ''}`}>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!selectedId || changing}
                        onClick={() => changeDistributor()}
                    >
                        {changing && <Spinner label={__('Changing distributor...')} className="mr-2" />}
                        {__('Change Distributor')}
                    </button>
                </span>

                {confirmMessage &&
                    <div className="alert alert-warning mt-3 mb-0 demo-edit-distributor-confirm" role="alert">
                        <p>{confirmMessage}</p>
                        <button type="button" className="btn btn-primary btn-sm mr-2" onClick={() => changeDistributor(true)}>
                            {__('Confirm')}
                        </button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmMessage(null)}>
                            {__('Cancel')}
                        </button>
                    </div>
                }
            </div>
        </div>
    );
}
